import { useEffect, useCallback } from 'react';
import { useAlertStore, AlertType } from '../store/useAlertStore';

const TYPE_CONFIG: Record<AlertType, { color: string; bg: string; icon: React.ReactNode }> = {
  info: {
    color: '#3b82f6',
    bg: 'rgba(59,130,246,0.08)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" width="20" height="20">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="8" strokeWidth="3"/>
        <line x1="12" y1="12" x2="12" y2="16"/>
      </svg>
    ),
  },
  success: {
    color: '#10b981',
    bg: 'rgba(16,185,129,0.08)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
        <path d="M20 6L9 17l-5-5"/>
      </svg>
    ),
  },
  warning: {
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.08)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" width="20" height="20">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17" strokeWidth="3"/>
      </svg>
    ),
  },
  error: {
    color: '#ef4444',
    bg: 'rgba(239,68,68,0.08)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="20" height="20">
        <circle cx="12" cy="12" r="10"/>
        <line x1="15" y1="9" x2="9" y2="15"/>
        <line x1="9" y1="9" x2="15" y2="15"/>
      </svg>
    ),
  },
};

export function AlertDialog() {
  const { visible, title, message, type, confirmText, close } = useAlertStore();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!visible) return;
      if (e.key === 'Escape' || e.key === 'Enter') close();
    },
    [visible, close],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!visible) return null;

  const cfg = TYPE_CONFIG[type];

  return (
    <div className="confirm-overlay" onClick={close}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface, #fff)',
          border: '1px solid var(--border, #e5e7eb)',
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          padding: '28px 24px 20px',
          width: 320,
          animation: 'confirm-slide-in 0.18s ease',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0,
        }}
      >
        {/* 图标 */}
        <div style={{
          width: 52, height: 52, borderRadius: '50%',
          background: cfg.bg, color: cfg.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 14,
        }}>
          {cfg.icon}
        </div>

        {/* 标题 */}
        {title && (
          <div style={{
            fontSize: 15, fontWeight: 600, color: 'var(--text, #111827)',
            marginBottom: 6, textAlign: 'center',
          }}>
            {title}
          </div>
        )}

        {/* 消息 */}
        <div style={{
          fontSize: 13, color: 'var(--text2, #6b7280)',
          lineHeight: 1.6, textAlign: 'center',
          marginBottom: 20, whiteSpace: 'pre-wrap',
        }}>
          {message}
        </div>

        {/* 按钮 */}
        <button
          onClick={close}
          autoFocus
          style={{
            width: '100%', height: 36,
            background: cfg.color, color: '#fff',
            border: 'none', borderRadius: 8,
            fontSize: 13, fontWeight: 500, cursor: 'pointer',
            transition: 'filter .15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.filter = 'brightness(1.08)')}
          onMouseLeave={(e) => (e.currentTarget.style.filter = '')}
        >
          {confirmText}
        </button>
      </div>
    </div>
  );
}
